import { CustomAttributeDefinition } from '@provision/entity-types';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { DefaultOption, FormField } from 'src/components';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { usePlatformEntityOptions, useUserGroupOptions } from 'src/graphql';
import {
  CUSTOM_ATTRIBUTE_TYPE_OPTIONS,
  CustomAttribute,
  mapAttributesDefinitionGroups,
  parseOptionsInput,
  useCustomAttributeDefinitions,
} from 'src/graphql/entities/custom-attributes';

const commonProps = {
  textFieldProps: {
    variant: 'outlined',
  },
};

export type CustomAttributeFormValues = Partial<Omit<CustomAttribute, 'options'>> & {
  restrictAccess: boolean;
  options?: string;
  relatedEntity?: {
    id: string;
    label: string;
  };
  group?: {
    id: string;
    label: string;
  };
  restrictedToUserGroups?: {
    id: string;
    label: string;
  }[];
};

export function useCustomAttributeForm(
  customAttributeDefinition?: CustomAttributeDefinition | null,
  formValues?: CustomAttributeFormValues,
) {
  const { customAttributeDefinitions } = useCustomAttributeDefinitions({
    partnerId: customAttributeDefinition?.partnerId,
    entity: customAttributeDefinition?.entity,
  });

  const [type, setType] = useState<DefaultOption | DefaultOption[] | null>(
    formValues?.type ?? null,
  );

  useEffect(() => {
    if (formValues?.type) {
      setType(formValues.type);
    }
  }, [formValues?.type]);

  const groups = useMemo(
    () =>
      customAttributeDefinitions
        ? mapAttributesDefinitionGroups(customAttributeDefinitions)
            // if both the partner and the platform have a group with the same name, then we will user the partners version instead.
            .filter(
              (group, index, arr) =>
                !arr.some(
                  (g, idx) =>
                    g.displayName === group.displayName &&
                    g.entity === group.entity &&
                    idx !== index &&
                    group.partnerId === PLATFORM_PARTNER_ID,
                ),
            )
            .map((group) => ({
              label: group.displayName,
              id: group.key,
            }))
        : [],
    [customAttributeDefinitions],
  );

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const { userGroupOptions, loading: userGroupOptionsLoading } = useUserGroupOptions();
  const { platformEntityOptions, loading: platformEntityOptionsLoading } =
    usePlatformEntityOptions();

  const attributeFormFields = useMemo(
    () =>
      [
        {
          type: 'textfield',
          name: 'displayName' as const,
          label: 'Property Name',
          required: 'Property name is required',
          textFieldProps: {
            ...commonProps.textFieldProps,
            autoFocus: true,
          },
        },
        {
          type: 'autocomplete',
          name: 'group' as const,
          label: 'Property Group',
          textFieldProps: {
            ...commonProps.textFieldProps,
          },
          options: groups,
        },
        {
          type: 'autocomplete',
          name: 'type' as const,
          label: 'Type',
          required: 'Type is required',
          textFieldProps: {
            ...commonProps.textFieldProps,
          },
          autocompleteProps: {
            loading: userGroupOptionsLoading,
            onChange: (_, value) => {
              setType(value);
            },
          },
          options: CUSTOM_ATTRIBUTE_TYPE_OPTIONS,
        },
        {
          type: 'textfield',
          name: 'options' as const,
          label: 'Options',
          required: 'Options is required',
          textFieldProps: {
            ...commonProps.textFieldProps,
            multiline: true,
          },
          onChange: ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setSelectedOptions(parseOptionsInput(target?.value));
          },
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['multi_select', 'dropdown'],
              },
            },
          ],
        },
        {
          type: 'autocomplete',
          name: 'relatedEntity' as const,
          label: 'Related Entity',
          required: 'Related Entity is required',
          textFieldProps: {
            ...commonProps.textFieldProps,
          },
          autocompleteProps: {
            loading: platformEntityOptionsLoading,
          },
          options: platformEntityOptions,
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['lookup_relational'],
              },
            },
          ],
        },
        {
          type: 'textfield',
          name: 'defaultValue' as const,
          label: 'Default Value',
          textFieldProps: {
            ...commonProps.textFieldProps,
            inputProps: {
              type: getInputType(type),
            },
            InputLabelProps: {
              // https://mui.com/material-ui/react-text-field/#shrink
              shrink: true,
            },
          },
          dependsOn: ['type'],
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['short_text', 'multi_line_text', 'number'],
              },
            },
          ],
        },
        {
          type: 'autocomplete',
          name: 'defaultValue' as const,
          label: 'Default Value',
          textFieldProps: {
            ...commonProps.textFieldProps,
          },
          dependsOn: ['type'],
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['multi_select', 'dropdown'],
              },
            },
          ],
          options: selectedOptions,
        },
        {
          type: 'switch',
          name: 'defaultValue' as const,
          label: 'Default Value',
          labelPlacement: 'top',
          trueLabel: 'True',
          falseLabel: 'False',
          dependsOn: ['type'],
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['checkbox'],
              },
            },
          ],
        },
        {
          type: 'switch',
          name: 'restrictAccess' as const,
          label: 'Restrict access',
        },
        {
          type: 'autocomplete',
          name: 'restrictedToUserGroups' as const,
          label: 'Groups with access',
          autocompleteProps: {
            multiple: true,
            disableCloseOnSelect: true,
          },
          dependsOn: ['restrictAccess'],
          textFieldProps: {
            ...commonProps.textFieldProps,
          },
          options: userGroupOptions,
          showsIf: [
            {
              name: 'restrictAccess',
              value: true,
            },
          ],
        },
      ] as FormField[],
    [
      groups,
      userGroupOptionsLoading,
      platformEntityOptionsLoading,
      platformEntityOptions,
      type,
      selectedOptions,
      userGroupOptions,
    ],
  );

  return { attributeFormFields };
}

function getInputType(type?: DefaultOption | DefaultOption[] | null) {
  if (!type) {
    return 'text';
  }

  if (typeof type === 'object' && 'id' in type) {
    return type?.id;
  }

  return 'text';
}
